import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './PersonaCard.scss';

const PersonaCard = ({ persona, index }) => {
  const [expanded, setExpanded] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);
  const [copiedScript, setCopiedScript] = useState(null);

  const handleCopyUrl = (url, type) => {
    navigator.clipboard.writeText(url);
    setCopiedUrl(type);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const handleCopyScript = (script, scriptIndex) => {
    navigator.clipboard.writeText(script);
    setCopiedScript(scriptIndex);
    setTimeout(() => setCopiedScript(null), 2000);
  };

  const getMessageTypeLabel = (type) => {
    const labels = {
      connection: 'Connection Request',
      follow_up: 'Follow-up Message',
      inmail: 'InMail Template',
    };
    return labels[type] || type;
  };

  const getMessageTypeBadgeClass = (type) => {
    const classes = {
      connection: 'badge-connection',
      follow_up: 'badge-followup',
      inmail: 'badge-inmail',
    };
    return classes[type] || 'badge-default';
  };

  return (
    <div className="persona-card">
      <div className="persona-card-header">
        <div className="persona-number">#{index + 1}</div>
        <h3 className="persona-name">{persona.personaName}</h3>
      </div>

      <div className="persona-section">
        <h4 className="section-title">Job Titles</h4>
        <ul className="persona-list">
          {persona.jobTitles?.slice(0, expanded ? undefined : 3).map((title) => (
            <li key={title}>{title}</li>
          ))}
        </ul>
        {persona.jobTitles?.length > 3 && (
          <button type="button" className="expand-btn" onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Show less' : `+${persona.jobTitles.length - 3} more`}
          </button>
        )}
      </div>

      {persona.industries && persona.industries.length > 0 && (
        <div className="persona-section">
          <h4 className="section-title">Industries</h4>
          <div className="tag-container">
            {persona.industries.map((industry) => (
              <span key={industry} className="tag">
                {industry}
              </span>
            ))}
          </div>
        </div>
      )}

      {persona.companySizes && persona.companySizes.length > 0 && (
        <div className="persona-section">
          <h4 className="section-title">Company Size</h4>
          <div className="tag-container">
            {persona.companySizes.map((size) => (
              <span key={size} className="tag tag-primary">
                {size}
              </span>
            ))}
          </div>
        </div>
      )}

      {persona.painPoints && persona.painPoints.length > 0 && (
        <div className="persona-section">
          <h4 className="section-title">Pain Points</h4>
          <ul className="persona-list pain-points-list">
            {persona.painPoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </div>
      )}

      {persona.messagingFramework && (
        <div className="persona-section messaging-section">
          <h4 className="section-title">Messaging Framework</h4>

          {persona.messagingFramework.hooks && persona.messagingFramework.hooks.length > 0 && (
            <div className="messaging-item">
              <strong>Hooks:</strong>
              <ul className="messaging-list">
                {persona.messagingFramework.hooks.map((hook, idx) => (
                  <li key={idx}>{hook}</li>
                ))}
              </ul>
            </div>
          )}

          {persona.messagingFramework.valuePropositions && persona.messagingFramework.valuePropositions.length > 0 && (
            <div className="messaging-item">
              <strong>Value Propositions:</strong>
              <ul className="messaging-list">
                {persona.messagingFramework.valuePropositions.map((vp, idx) => (
                  <li key={idx}>{vp}</li>
                ))}
              </ul>
            </div>
          )}

          {persona.messagingFramework.ctaExamples && persona.messagingFramework.ctaExamples.length > 0 && (
            <div className="messaging-item">
              <strong>Call-to-Action Examples:</strong>
              <ul className="messaging-list">
                {persona.messagingFramework.ctaExamples.map((cta, idx) => (
                  <li key={idx}>{cta}</li>
                ))}
              </ul>
            </div>
          )}

          {persona.messagingFramework.toneGuidelines && (
            <div className="messaging-item">
              <strong>Tone Guidelines:</strong>
              <p>{persona.messagingFramework.toneGuidelines}</p>
            </div>
          )}
        </div>
      )}

      {persona.scriptTemplates && persona.scriptTemplates.length > 0 && (
        <div className="persona-section script-templates-section">
          <h4 className="section-title">Ready-to-Use Scripts</h4>
          <p className="section-subtitle">Copy and paste these directly into LinkedIn or your sequences</p>

          <div className="script-templates-grid">
            {persona.scriptTemplates.map((template, idx) => (
              <div key={idx} className="script-template-card">
                <div className="script-header">
                  <span className={`message-type-badge ${getMessageTypeBadgeClass(template.messageType)}`}>
                    {getMessageTypeLabel(template.messageType)}
                  </span>
                  <span className="character-count">{template.characterCount} chars</span>
                </div>

                <h5 className="script-name">{template.name}</h5>

                <div className="script-content">
                  <p>{template.script}</p>
                </div>

                <button
                  type="button"
                  className="btn-copy-script"
                  onClick={() => handleCopyScript(template.script, idx)}
                >
                  {copiedScript === idx ? '✓ Copied to Clipboard!' : '📋 Copy Script'}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="persona-actions">
        {persona.salesNavigatorSearchUrl && (
          <>
            <button
              type="button"
              className="btn-action btn-primary-action"
              onClick={() => window.open(persona.salesNavigatorSearchUrl, '_blank')}
            >
              🔍 Open in Sales Navigator
            </button>
            <button
              type="button"
              className="btn-action btn-secondary-action"
              onClick={() => handleCopyUrl(persona.salesNavigatorSearchUrl, 'salesNav')}
            >
              {copiedUrl === 'salesNav' ? '✓ Copied!' : '📋 Copy URL'}
            </button>
          </>
        )}

        {persona.linkedInSearchUrl && !persona.salesNavigatorSearchUrl && (
          <>
            <button
              type="button"
              className="btn-action btn-primary-action"
              onClick={() => window.open(persona.linkedInSearchUrl, '_blank')}
            >
              🔍 Open in LinkedIn
            </button>
            <button
              type="button"
              className="btn-action btn-secondary-action"
              onClick={() => handleCopyUrl(persona.linkedInSearchUrl, 'linkedin')}
            >
              {copiedUrl === 'linkedin' ? '✓ Copied!' : '📋 Copy URL'}
            </button>
          </>
        )}
      </div>

      {persona.salesNavigatorSearchUrl && (
        <div className="url-preview">
          <small>Sales Navigator URL:</small>
          <code>{persona.salesNavigatorSearchUrl.substring(0, 80)}...</code>
        </div>
      )}
    </div>
  );
};

PersonaCard.propTypes = {
  persona: PropTypes.shape({
    personaName: PropTypes.string,
    jobTitles: PropTypes.arrayOf(PropTypes.string),
    industries: PropTypes.arrayOf(PropTypes.string),
    companySizes: PropTypes.arrayOf(PropTypes.string),
    painPoints: PropTypes.arrayOf(PropTypes.string),
    messagingFramework: PropTypes.shape({
      hooks: PropTypes.arrayOf(PropTypes.string),
      valuePropositions: PropTypes.arrayOf(PropTypes.string),
      ctaExamples: PropTypes.arrayOf(PropTypes.string),
      toneGuidelines: PropTypes.string,
    }),
    scriptTemplates: PropTypes.arrayOf(
      PropTypes.shape({
        messageType: PropTypes.oneOf(['connection', 'follow_up', 'inmail']),
        name: PropTypes.string,
        script: PropTypes.string,
        characterCount: PropTypes.number,
      }),
    ),
    salesNavigatorSearchUrl: PropTypes.string,
    linkedInSearchUrl: PropTypes.string,
  }).isRequired,
  index: PropTypes.number.isRequired,
};

export default PersonaCard;
